import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { BrandL, BrandS, LabelM } from '@sorare/core/src/atoms/typography';
import { Avatar } from '@sorare/core/src/components/user/Avatar';
import { ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_CONTENDER_CONTENDER } from '@sorare/core/src/constants/__generated__/routes';
import { useEventsContext } from '@sorare/core/src/contexts/events';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import { useInfiniteScroll } from '@sorare/core/src/hooks/useInfiniteScroll';
import { isType } from '@sorare/core/src/lib/gql';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { Contender } from 'components/lineup/Contender';

import { ChampionshipLineupPosition } from '../ChampionshipLineupPosition';
import { ChampionshipNoLineups } from '../ChampionshipNoLineups';
import type {
  ChampionshipUserLineups_so5UserGroup,
  ChampionshipUserLineups_user,
  UserGroupContendersQuery,
  UserGroupContendersQueryVariables,
} from './__generated__/index.graphql';

const Root = styled(Vertical).attrs({ gap: 2 })`
  padding: var(--double-unit);

  &.loading {
    height: 300px;
  }
`;
const Header = styled(SBHorizontal)`
  width: 100%;
  align-items: flex-end;
`;
const ContendersGrid = styled(Horizontal)`
  flex-wrap: wrap;

  @media ${tabletAndAbove} {
    & > * {
      max-width: calc(50% - var(--half-unit));
    }
  }
`;
const LeaderboardPosition = styled.div`
  display: grid;
  grid-template-areas: 'pos th .' 'pos . total';
  grid-template-columns: min-content min-content 1fr;
  column-gap: var(--half-unit);

  & > .pos {
    grid-area: pos;
  }
  & > .th {
    grid-area: th;
  }
  & > .total {
    grid-area: total;
  }
`;

const USER_GROUP_CONTENDERS_QUERY = gql`
  query UserGroupContendersQuery(
    $userGroupSlug: String
    $userSlug: String!
    $after: String
  ) {
    so5 {
      so5UserGroup(slug: $userGroupSlug) {
        slug
        id
        membershipsCount
        membership(userSlug: $userSlug) {
          id
          ... on So5UserGroupMembership {
            id
            overallRanking
            overallScore
            topSo5LeaderboardContenders {
              slug
              ...ChampionshipLineupPosition_so5LeaderboardContender
            }
            so5LeaderboardContenders(first: 10, after: $after) {
              nodes {
                slug
                so5Leaderboard {
                  slug
                  id
                  so5Fixture {
                    slug
                    type
                  }
                }
                so5Lineup {
                  id
                }
                ...Contender_so5LeaderboardContender
                ...ChampionshipLineupPosition_so5LeaderboardContender
              }
              pageInfo {
                endCursor
                hasNextPage
              }
            }
          }
        }
      }
    }
  }
  ${Contender.fragments.so5LeaderboardContender}
  ${ChampionshipLineupPosition.fragments.so5LeaderboardContender}
` as TypedDocumentNode<
  UserGroupContendersQuery,
  UserGroupContendersQueryVariables
>;

type Props = {
  user: ChampionshipUserLineups_user;
  so5UserGroup: ChampionshipUserLineups_so5UserGroup;
};
export const ChampionshipUserLineups = ({ user, so5UserGroup }: Props) => {
  const { sport, generateSportContextPath } = useSportContext();
  const { track } = useEventsContext();

  const { data, loading, loadMore } = usePaginatedQuery(
    USER_GROUP_CONTENDERS_QUERY,
    {
      variables: {
        userGroupSlug: so5UserGroup.slug,
        userSlug: user.slug,
      },
      connection: 'So5LeaderboardContenderConnection',
    }
  );

  const fetchedUserGroup = data?.so5.so5UserGroup;
  const so5UserGroupMembership = isType(
    fetchedUserGroup?.membership,
    'So5UserGroupMembership'
  )
    ? fetchedUserGroup.membership
    : null;

  const { so5LeaderboardContenders, topSo5LeaderboardContenders } =
    so5UserGroupMembership || {};
  const { pageInfo, nodes = [] } = so5LeaderboardContenders || {};

  const { InfiniteScrollLoader } = useInfiniteScroll(
    () => {
      loadMore(false, { after: pageInfo?.endCursor });
    },
    !!pageInfo?.hasNextPage,
    loading
  );

  if (loading) {
    return (
      <Root className="loading">
        <LoadingIndicator small />
      </Root>
    );
  }
  if (!so5UserGroupMembership) {
    return (
      <Root>
        <ChampionshipNoLineups
          userGroupSlug={so5UserGroup.slug}
          onComposeClick={() =>
            track(`Click Chase Championship Compose Button`, {
              user_group_slug: so5UserGroup.slug,
              sport,
            })
          }
          onMarketClick={() =>
            track(`Click Chase Championship Market Button`, {
              user_group_slug: so5UserGroup.slug,
              sport,
            })
          }
          nbCards={sport === Sport.BASEBALL ? 7 : 5}
        />
      </Root>
    );
  }

  return (
    <Root>
      <Header>
        <Horizontal>
          <Avatar user={user} rounded size={7} />
          <Vertical gap={0.5}>
            <LabelM color="var(--c-nd-600)">{user.nickname}</LabelM>
            <LeaderboardPosition>
              {!!so5UserGroupMembership.overallRanking && (
                <>
                  <BrandL className="pos">
                    <FormattedNumber
                      value={so5UserGroupMembership.overallRanking}
                    />
                  </BrandL>
                  <BrandS className="th" color="var(--c-nd-600)">
                    <FormattedMessage
                      id="ChampionshipUserLineups.positionOrdinal"
                      defaultMessage="{n, selectordinal, one {st} two {nd} few {rd} other {th}}"
                      values={{ n: so5UserGroupMembership.overallRanking }}
                    />
                  </BrandS>
                </>
              )}
              <LabelM className="total" color="var(--c-nd-600)">
                <Horizontal as="span" gap={0.5}>
                  <span>/</span>
                  {!!fetchedUserGroup?.membershipsCount && (
                    <FormattedNumber
                      value={fetchedUserGroup.membershipsCount}
                    />
                  )}
                </Horizontal>
              </LabelM>
            </LeaderboardPosition>
          </Vertical>
        </Horizontal>
        <Horizontal>
          <BrandL>{so5UserGroupMembership.overallScore}</BrandL>
          <BrandS color="var(--c-nd-600)">
            <FormattedMessage
              id="ChampionshipUserLineups.pts"
              defaultMessage="pts"
            />
          </BrandS>
        </Horizontal>
      </Header>
      <ContendersGrid>
        {nodes.map(so5LeaderboardContender => {
          const { so5Leaderboard } = so5LeaderboardContender || {};

          return (
            <Contender
              key={so5LeaderboardContender.slug}
              so5LeaderboardContender={so5LeaderboardContender}
              hideSubFooter
              customFooter={
                !!topSo5LeaderboardContenders && (
                  <ChampionshipLineupPosition
                    topSo5LeaderboardContenders={topSo5LeaderboardContenders}
                    so5LeaderboardContender={so5LeaderboardContender}
                  />
                )
              }
              to={generateSportContextPath(
                ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_CONTENDER_CONTENDER,
                {
                  params: {
                    eventType: so5Leaderboard.so5Fixture.type.toLowerCase(),
                    fixture: so5Leaderboard.so5Fixture.slug,
                    contender: so5LeaderboardContender.slug,
                  },
                }
              )}
            />
          );
        })}
      </ContendersGrid>
      <InfiniteScrollLoader />
    </Root>
  );
};

ChampionshipUserLineups.fragments = {
  user: gql`
    fragment ChampionshipUserLineups_user on User {
      slug
      id
      nickname
      ...Avatar_user
    }
    ${Avatar.fragments.user}
  ` as TypedDocumentNode<ChampionshipUserLineups_user>,
  so5UserGroup: gql`
    fragment ChampionshipUserLineups_so5UserGroup on So5UserGroup {
      slug
      id
    }
  ` as TypedDocumentNode<ChampionshipUserLineups_so5UserGroup>,
};
